import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';

// API URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Initial state
const initialState = {
  tickets: [],
  loading: false,
  exporting: false,
  error: null,
  totalTickets: 0,
  totalPages: 0,
  currentPage: 1,
  filters: {
    user_id: '',
    giveaway_id: '',
    status: '',
    tier: '',
  },
};

// Get all tickets
export const getTickets = createAsyncThunk(
  'tickets/getTickets',
  async ({ page = 1, perPage = 20, user_id = '', giveaway_id = '', status = '', tier = '' }, { rejectWithValue }) => {
    try {
      let url = `${API_URL}/tickets?page=${page}&per_page=${perPage}`;
      
      if (user_id) {
        url += `&user_id=${user_id}`;
      }
      
      if (giveaway_id) {
        url += `&giveaway_id=${giveaway_id}`;
      }
      
      if (status) {
        url += `&status=${status}`;
      }
      
      if (tier) {
        url += `&tier=${tier}`;
      }
      
      const response = await axios.get(url);
      
      return {
        tickets: response.data,
        totalTickets: parseInt(response.headers['x-wp-total'] || 0),
        totalPages: parseInt(response.headers['x-wp-totalpages'] || 0),
        currentPage: page,
        filters: { user_id, giveaway_id, status, tier },
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch tickets';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Get member tickets
export const getMemberTickets = createAsyncThunk(
  'tickets/getMemberTickets',
  async ({ id, page = 1, perPage = 20, giveaway_id = '', status = '' }, { rejectWithValue }) => {
    try {
      let url = `${API_URL}/tickets/member/${id}?page=${page}&per_page=${perPage}`;
      
      if (giveaway_id) {
        url += `&giveaway_id=${giveaway_id}`;
      }
      
      if (status) {
        url += `&status=${status}`;
      }
      
      const response = await axios.get(url);
      
      return {
        tickets: response.data,
        totalTickets: parseInt(response.headers['x-wp-total'] || 0),
        totalPages: parseInt(response.headers['x-wp-totalpages'] || 0),
        currentPage: page,
        filters: { user_id: id, giveaway_id, status },
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch member tickets';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Create ticket
export const createTicket = createAsyncThunk(
  'tickets/createTicket',
  async (ticketData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/tickets`, ticketData);
      toast.success('Ticket created successfully');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create ticket';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Export tickets to CSV
export const exportTickets = createAsyncThunk(
  'tickets/exportTickets',
  async (filters, { rejectWithValue }) => {
    try {
      let url = `${API_URL}/tickets/export`;
      
      // Add filters to query string
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });
      
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
      
      const response = await axios.get(url, {
        responseType: 'blob',
      });
      
      // Create a download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `tickets-export-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Tickets exported successfully');
      return true;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to export tickets';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Tickets slice
const ticketsSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    resetTicketsError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get tickets cases
      .addCase(getTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTickets.fulfilled, (state, action) => {
        state.loading = false;
        state.tickets = action.payload.tickets;
        state.totalTickets = action.payload.totalTickets;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.filters = action.payload.filters;
        state.error = null;
      })
      .addCase(getTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get member tickets cases
      .addCase(getMemberTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMemberTickets.fulfilled, (state, action) => {
        state.loading = false;
        state.tickets = action.payload.tickets;
        state.totalTickets = action.payload.totalTickets;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.filters = action.payload.filters;
        state.error = null;
      })
      .addCase(getMemberTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create ticket cases
      .addCase(createTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTicket.fulfilled, (state, action) => {
        state.loading = false;
        state.tickets = [action.payload, ...state.tickets];
        state.totalTickets += 1;
        state.error = null;
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Export tickets cases
      .addCase(exportTickets.pending, (state) => {
        state.exporting = true;
        state.error = null;
      })
      .addCase(exportTickets.fulfilled, (state) => {
        state.exporting = false;
        state.error = null;
      })
      .addCase(exportTickets.rejected, (state, action) => {
        state.exporting = false;
        state.error = action.payload;
      });
  },
});

export const { resetTicketsError, setFilters } = ticketsSlice.actions;

export default ticketsSlice.reducer;